import { geohashForLocation } from 'geofire-common';

import Constants from '@helpers/Constants';
import { get, post } from '@helpers/Http';
import { Place } from '@types';

const API_KEY = Constants.joinstar.googleMapsPlatformAPIKey;

export type PlaceAutocomplete = {
  suggestions: PlaceSuggestion[];
};

export type PlaceAutocompleteResult = Pick<Place, 'displayName' | 'placeId'>;

export type PlaceCoordinate = {
  latitude: number;
  longitude: number;
};

export type PlaceViewport = {
  low: PlaceCoordinate;
  high: PlaceCoordinate;
};

export type PlaceAutocompleteSelection = {
  place: Place;
  viewport?: PlaceViewport;
};

export type PlaceSuggestion = {
  placePrediction: {
    placeId: string;
    text: {
      text: string;
    };
  };
};

export async function getPlaceAutocomplete(
  query: string,
  types?: string | string[],
  token?: string
) {
  const response = await post(
    'https://places.googleapis.com/v1/places:autocomplete',
    {
      input: query,
      includedPrimaryTypes: typeof types === 'string' ? [types] : types,
      sessionToken: token,
    },
    {
      headers: {
        'X-Goog-Api-Key': API_KEY,
        'Content-Type': 'application/json',
      },
    }
  );
  return response.data as PlaceAutocomplete;
}

export function getPlaceAutocompleteResults(
  autocomplete: PlaceAutocomplete
): PlaceAutocompleteResult[] {
  return (
    autocomplete.suggestions
      ?.filter((suggestion) => suggestion.placePrediction)
      .map((suggestion) => ({
        placeId: suggestion.placePrediction.placeId,
        displayName: suggestion.placePrediction.text.text,
      })) || []
  );
}

export type PlaceDetail = {
  location: PlaceCoordinate;
  viewport?: PlaceViewport;
};

export async function getPlaceDetail(placeId: string, token?: string) {
  const response = await get(`https://places.googleapis.com/v1/places/${placeId}`, {
    params: {
      key: API_KEY,
      fields: 'location,viewport',
      sessionToken: token,
    },
  });
  return response.data as PlaceDetail;
}

export async function getPlaceFromAutocompleteResult(
  result: PlaceAutocompleteResult,
  token?: string
): Promise<PlaceAutocompleteSelection> {
  const detail = await getPlaceDetail(result.placeId, token);
  const hash = geohashForLocation([detail.location.latitude, detail.location.longitude]);

  return {
    place: { ...result, ...detail.location, geohash: hash },
    viewport: detail.viewport,
  };
}

export function validatePlace(place?: Place) {
  let message = 'Please select a place from the search results.';
  if (!place) return message;
  if (!(place.placeId && place.placeId.length > 0)) return message;
  if (!(place.displayName && place.displayName.length > 0)) return message;

  // these properties are added by a place details query
  // after the user selects a search result, which could fail
  message = 'An error occurred, please try the search again.';
  if (!(place.latitude && place.longitude && place.geohash && place.geohash.length > 0)) {
    return message;
  }

  return '';
}
